/**
 * @suite/tour — the handler behind a settings surface's "Replay the
 * first-run tour" row (docs/product/onboarding-tour README §3.6).
 *
 * One hook, both settings surfaces:
 *   - main document (the in-app settings dialog): close the surface first so
 *     the tour overlay owns the screen alone, then start;
 *   - the separate settings window (`?view=settings`): the tour targets live
 *     in the main document, not here — hand the replay to the main window
 *     (replay.ts relay) and close this window.
 */
import { useCallback, useRef } from "react";
import { closeSettingsWindow, isSettingsWindowDocument } from "@suite/settings";
import { useTourOptional } from "./TourContext";
import { requestTourReplay } from "./replay";
import type { TourConfig, TourLabels } from "./types";

/**
 * Build the row's click handler. `close` closes the caller's settings
 * surface (dialog close, store flag, …); the settings-window branch closes
 * the window itself. Config/labels are re-read through refs, so a host
 * rebuilding them with live `t()` strings stays current without re-arming.
 */
export function useTourReplayEntry(
  config: TourConfig,
  labels: TourLabels | undefined,
  close: () => void
): () => void {
  const tour = useTourOptional();
  const configRef = useRef(config);
  configRef.current = config;
  const labelsRef = useRef(labels);
  labelsRef.current = labels;
  const closeRef = useRef(close);
  closeRef.current = close;

  return useCallback(() => {
    if (isSettingsWindowDocument()) {
      requestTourReplay(configRef.current.appId);
      closeSettingsWindow();
      return;
    }
    closeRef.current();
    tour?.start(configRef.current, labelsRef.current);
  }, [tour]);
}
